import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import _ from "lodash";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";
import Header from "./commons/Header";
import Sider from "./commons/Sider";
import RightPanel from "./parts/RightPanel";
import CommandPalette from "./parts/CommandPalette";
import Toasts from "./parts/Toasts";
import ConfirmDialog from "./parts/ConfirmDialog";
import DailyTaskBanner from "./parts/DailyTaskBanner";
import { AppCtx } from "../design/AppContext";
import Backdrop from "@mui/material/Backdrop";
import Box from "@mui/material/Box";
import { PAGE_TITLES, routeKeyOf, routePermissionOf } from "../resources/menu";
import NoPermission from "../components/auth/NoPermission";
import { getTheme, setTheme as persistTheme } from "../utils/theme";
import useChat from "../hook/useChat";
import useNotification from "../hook/useNotification";
import useScrollRestoration from "../hook/useScrollRestoration";

/**
 * 應用外殼 — 骨架對應 el-pm-template_4.html:453-486：
 * 外框 > 側邊欄 + 主區（頁首 + 內容區 section）。
 *
 * 側邊欄與頁首由那兩支元件各自負責，這裡只負責外框、內容區內距，
 * 以及把收合狀態交給側邊欄。
 * 另外提供 Toast / 刪除確認 / 命令面板 / 使用導覽 給所有頁面（useApp）。
 */
function Layout(props) {
  const location = useLocation();
  const navigate = useNavigate();
  // 換頁捲到頂、按上一頁回到離開時的位置（見 hook 內說明）
  useScrollRestoration();

  const [theme, setThemeState] = useState(() => getTheme());
  const [wide, setWide] = useState(() => window.innerWidth >= 900);
  const [mnav, setMnav] = useState(false);
  const [panel, setPanel] = useState(null);
  const [cmdOpen, setCmdOpen] = useState(false);
  const [toasts, setToasts] = useState([]);
  /*
   * 只留 setter：這個值原本唯一的讀者是使用導覽（tourSteps 要靠它分辨專案詳情
   * 的子檢視），導覽下架後就沒有人讀了。但 setDetailView 仍然掛在 AppCtx 上、
   * 專案詳情那幾頁還在呼叫，所以 state 本身要留著，不能一起拿掉。
   */
  const [, setDetailView] = useState("list");
  const [confirmLogout, setConfirmLogout] = useState(false);
  const [confirmState, setConfirmState] = useState(null);
  const [chatInput, setChatInput] = useState("");
  const toastId = useRef(0);

  /* ---------- 佈景 ---------- */
  useEffect(() => {
    persistTheme(theme);
  }, [theme]);

  /*
   * 反過來也要跟：「設定 → 偏好設定」也能切深淺色，它直接改 <html data-theme>，
   * 不經過這裡的 state。不跟的話，那邊切成深色之後頁首與手機抽屜還寫著「深色」，
   * 按下去 state 從（過時的）淺色翻成深色 —— 畫面毫無反應，看起來就是按鈕壞了。
   * 偏好設定頁那邊早就是盯著 <html> 的屬性（Preferences.js 的 MutationObserver），
   * 這裡用同一招，兩個入口才是同一份資料。
   *
   * 不會繞成迴圈：上面的 persistTheme 寫回同一個值時觀察者雖然會再叫一次，
   * 但 setState 同值 React 直接略過，不會再觸發一次 effect。
   */
  useEffect(() => {
    const el = document.documentElement;
    const mo = new MutationObserver(() => {
      setThemeState(el.getAttribute("data-theme") === "dark" ? "dark" : "light");
    });
    mo.observe(el, { attributes: true, attributeFilter: ["data-theme"] });
    return () => mo.disconnect();
  }, []);

  const toggleTheme = () => setThemeState((t) => (t === "dark" ? "light" : "dark"));

  /* ---------- 響應式 ---------- */
  useEffect(() => {
    const onResize = () => setWide(window.innerWidth >= 900);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  /* 從窄拉到寬時把抽屜狀態歸零。桌機看不出這個狀態（側欄常駐、沒有遮罩），
     但它留著 true 的話，再縮回窄版時抽屜會自己跳出來蓋住內容。 */
  useEffect(() => {
    if (wide) setMnav(false);
  }, [wide]);

  /* ---------- ⌘K / ESC ---------- */
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === "k" || e.key === "K")) {
        e.preventDefault();
        setCmdOpen((v) => !v);
      }
      if (e.key === "Escape") {
        setCmdOpen(false);
        setPanel(null);
        // 手機抽屜跟其他浮層一樣，ESC 就關（點遮罩關是給手指的，鍵盤要有自己的路）
        setMnav(false);
      }
    };
    const onOpenCmd = () => setCmdOpen(true);
    window.addEventListener("keydown", onKey);
    window.addEventListener("app:open-cmd", onOpenCmd);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("app:open-cmd", onOpenCmd);
    };
  }, []);

  /* ---------- 路由變化時收起浮層 ---------- */
  useEffect(() => {
    setMnav(false);
    setPanel(null);
    setCmdOpen(false);
  }, [location.pathname]);

  /* ---------- 通知 / 聊天 ---------- */
  const {
    messages: chatMessages,
    onlineUsers,
    connected: chatConnected,
    sendMessage,
    newMessageCount,
    resetNewCount: resetChatCount,
    loadMoreMessages,
    loadingMore: chatLoadingMore,
    hasMore: chatHasMore,
  } = useChat();
  const {
    notifications,
    unread: notifyUnread,
    loadHistory,
    loadMore: loadMoreNotifications,
    resetNewCount: resetNotifyCount,
    markAllRead: markAllNotifyRead,
    markRead: markNotifyRead,
  } = useNotification({
    /* 只轉呼叫 ref 裡的函式：toast 定義在下面，直接傳會踩到 TDZ（見該處說明） */
    onIncoming: (n) => notifyToastRef.current && notifyToastRef.current(n),
  });

  /* 收到即時通知時要跳的 toast。實際函式在 toast 定義之後才填進來（見下方 effect） */
  const notifyToastRef = useRef(null);

  const activities = _.get(props, "activities", []);
  const historyLoadedRef = useRef(false);
  useEffect(() => {
    if (activities.length > 0 && !historyLoadedRef.current) {
      loadHistory(activities);
      historyLoadedRef.current = true;
    }
  }, [activities, loadHistory]);

  /* ---------- Toast ---------- */
  /* ms 不給就沿用 2.6 秒 —— 全站既有的呼叫端都是兩個參數，行為不能被改掉。
     通知的 toast 另外指定 5 秒：那是主動推播過來的訊息，不像操作回饋那樣
     使用者本來就在等，掃過去要多一點時間。 */
  /* 關閉（自動或按 X）先標成 leaving 讓收合動畫跑完，再從清單移除 */
  const dismissToast = useCallback((id) => {
    setToasts((s) => s.map((t) => (t.id === id ? { ...t, leaving: true } : t)));
    setTimeout(() => setToasts((s) => s.filter((t) => t.id !== id)), 250);
  }, []);
  const toast = useCallback(
    (msg, tone, ms) => {
      toastId.current += 1;
      const id = toastId.current;
      setToasts((s) => [...s, { id, msg, tone }]);
      setTimeout(() => dismissToast(id), ms || 2600);
    },
    [dismissToast]
  );

  /*
   * 收到即時通知時，右上角鈴鐺加數字之外，畫面上方也跳一則 toast。
   *
   * 透過 ref 轉一手，是因為 useNotification 在上面（L109）就呼叫了，
   * 而 toast 定義在這裡 —— 直接把 toast 傳進去會踩到 TDZ。
   */
  useEffect(() => {
    notifyToastRef.current = (n) => {
      const title = (n && (n.title || n.Title)) || "您有一則新通知";
      toast(title, undefined, 5000);
    };
  }, [toast]);

  /* ---------- 登入歡迎 Toast（設計稿 L1377 的 login() 行為） ---------- */
  useEffect(() => {
    const msg = sessionStorage.getItem("WELCOME_TOAST");
    if (!msg) return;
    sessionStorage.removeItem("WELCOME_TOAST");
    toast(msg);
  }, [toast]);

  /* ---------- 刪除確認 ---------- */
  const confirmDelete = useCallback((opts) => {
    setConfirmState({
      tone: "danger",
      title: opts.title || "確定刪除此工項？",
      subtitle: opts.subtitle || "此操作無法復原",
      body: opts.body,
      cancelText: "取消",
      confirmText: opts.confirmText || "刪除",
      onConfirm: opts.onConfirm,
    });
  }, []);

  /*
   * 讓 utils/exception.js 也能發 toast。
   *
   * 它是給 saga 用的純函式，拿不到 React context，直接 import store 又會繞回來
   * 變成循環相依，所以掛在 window 上。Layout 沒掛載時（登入頁）自然拿不到，
   * exception 那邊會退回 window.alert，訊息不會不見。
   */
  useEffect(() => {
    window.__EL_TOAST__ = (content) => toast(content, "error");
    return () => { delete window.__EL_TOAST__; };
  }, [toast]);

  /* ---------- Message API（沿用既有 saga 介面） ---------- */
  useEffect(() => {
    props.SAVE_Message({
      success: (content) => toast(content),
      error: (content) => toast(content, "error"),
      warning: (content) => toast(content, "error"),
      info: (content) => toast(content),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toast]);

  /* ---------- 導覽 ---------- */
  const routeKey = routeKeyOf(location.pathname);

  /*
   * 路由層的權限守衛。
   *
   * 側欄藏起來的頁面原本直接打網址還是進得去，後端 [PermissionOnly] 會回 403，
   * 但畫面只會停在空白或紅字，看起來像壞掉。這裡用側欄同一份 menus 判斷，
   * 兩邊就不可能不一致。真正的把關仍然在後端，這一層只是把「你沒被授權」講清楚。
   *
   * 只在 isReady（權限已經載回來）之後才擋 —— 載入中就擋會讓每次重新整理都閃一下
   * 無權限畫面，那比不擋更糟。
   */
  const neededPermission = routePermissionOf(location.pathname);
  const permCodes = useMemo(
    () =>
      (props.permissions || []).map((p) => (typeof p === "string" ? p : _.get(p, "module", ""))),
    [props.permissions]
  );
  const denied =
    props.isReady &&
    Array.isArray(props.permissions) &&
    !!neededPermission &&
    !permCodes.includes(neededPermission);

  /* ---------- 頁面標題 ---------- */
  const pageTitle = useMemo(() => {
    if (routeKey === "proj_detail") {
      return _.get(props, "projectDetail.name") || PAGE_TITLES.proj_detail;
    }
    return PAGE_TITLES[routeKey] || "";
  }, [routeKey, props]);

  /* 頁首的人名鈕與「今天」的副標都要它；取法跟側欄一致（Sider.js:135），
     兩邊才不會一個叫得出名字、一個叫不出來。 */
  const userName = useMemo(
    () => _.get(props.userInfo, "name") || _.get(props.userInfo, "Name") || "",
    [props.userInfo]
  );

  /*
   * 頁面副標 —— 設計稿 TITLES（el-pm-template_4.html:2653-2676）的第二欄。
   *
   * 標題只說「你在哪個模組」（工項清單與工項詳情都叫「工項」），是哪一件事寫在副標。
   *
   * 這裡只組殼層真的拿得到的東西：今天的日期與使用者、通知未讀數、待我簽核件數、
   * 我的帳號的人名。設計稿其他頁的副標（「我負責的 · 還沒做完的」、「新 WEBPOS ·
   * 3 個里程碑」）是各頁自己的篩選條件與資料，Layout 並不知道 —— 硬湊就是假資料，
   * 一律回空字串，Header 的副標就整個不渲染。
   *
   * 專案詳情也回空字串：pageTitle 已經把專案名當標題了，副標再寫一次只是重複。
   */
  const pageSub = useMemo(() => {
    if (routeKey === "notify") {
      return notifyUnread > 0 ? `${notifyUnread} 則未讀` : "";
    }
    if (routeKey === "approvals") {
      return props.approvalsCount > 0 ? `待我審核 ${props.approvalsCount} 件` : "";
    }
    if (routeKey === "me") return userName;
    if (routeKey === "me_pwd") return "變更密碼";
    return "";
  }, [routeKey, userName, notifyUnread, props.approvalsCount]);

  const ctx = useMemo(
    () => ({
      toast,
      confirmDelete,
      openCmd: () => setCmdOpen(true),
      /*
       * 使用導覽已經下架（設計稿的畫面索引本來就寫明「使用導覽 4 步彈窗改成
       * 新手指南一頁」）。這個鍵保留成空函式而不是拿掉 —— AppCtx 的形狀有
       * 十幾支檔案在依賴，少一個鍵會讓還在呼叫的地方直接炸掉。
       */
      startTour: () => {},
      setDetailView,
      isMobile: !wide,
      userInfo: props.userInfo,
    }),
    [toast, confirmDelete, wide, props.userInfo]
  );

  /* ---------- 未登入 ---------- */
  const token = localStorage.getItem("TOKEN");
  if (!token) return <Navigate to="/Auth/Login" replace />;

  const handleSendChat = () => {
    if (!chatInput.trim()) return;
    sendMessage(chatInput.trim());
    setChatInput("");
  };

  return (
    <AppCtx.Provider value={ctx}>
      {/*
        這一層不再掛 data-theme：佈景只掛在 <html>（utils/theme.js 在做）。
        兩個地方各掛一份，浮層（對話框、Toast、右側面板都掛在 body 下）會吃到
        外層那一份以外的值，遲早對不起來。
      */}
      <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: "background.default", color: "text.primary" }}>
        {/* 手機遮罩。側欄抽屜是 z-index:80，遮罩必須剛好疊在它下面一層 */}
        <Backdrop open={!wide && mnav} onClick={() => setMnav(false)} sx={{ zIndex: 79 }} />

        {/*
          側邊欄永遠渲染、而且桌機固定是收合態（圖示在上、文字在下）。

          設計規則是「側邊欄收合後仍保留圖示＋文字，不可消失」
          （el-pm-template_4.html:450），收合態本來就同時有圖示與文字，
          88px 的軌道寬比 264px 省一大截版面，而七個入口全部看得見。

          頁首那顆「選單」鈕因此在桌機拿掉了：既然沒有展開態要切，留一顆只會切出
          一個更佔空間、資訊量卻一樣的版本，那顆鈕本身就是多的。

          手機是唯一的例外：窄螢幕的側邊欄是 position:fixed 的覆蓋式抽屜，
          常駐會整片蓋住內容，所以維持「遮罩＋抽屜」的既有行為 —— 也因此手機
          一定要有一顆鈕叫得出抽屜。選單鈕當初是整顆連 onToggleNav 一起刪的，
          結果 mnav 再也沒有人設成 true，手機上等於完全沒有導覽。
          現在它只在 !wide 時出現（Header 的 compact），桌機照舊沒有。

          手機頁首擠不下的「深色」「聊天」也搬進抽屜，所以這兩組 handler
          頁首與側欄各傳一份；Sider 只在 !wide 時畫它們。
        */}
        {(wide || mnav) && (
          <Sider
            wide={wide}
            collapsed={wide}
            onNavigate={() => setMnav(false)}
            onClose={() => setMnav(false)}
            approvalsCount={props.approvalsCount}
            onLogout={() => setConfirmLogout(true)}
            chatUnread={newMessageCount}
            onOpenChat={() => {
              // 右側面板是 MUI Drawer（z-index 1200），會疊在 z-index:80 的側欄抽屜上，
              // 不收也看得到面板；但面板關掉後抽屜還攤在那裡擋內容，所以開面板前先把抽屜收掉
              setMnav(false);
              setPanel("chat");
              resetChatCount();
            }}
          />
        )}

        {/* 主區 */}
        <Box sx={{ flex: "1 1 auto", minWidth: 0, display: "flex", flexDirection: "column" }}>
          <Header
            pageTitle={pageTitle}
            pageSub={pageSub}
            userName={userName}
            compact={!wide}
            navOpen={mnav}
            onToggleNav={() => setMnav((v) => !v)}
            isDark={theme === "dark"}
            onToggleTheme={toggleTheme}
            unreadCount={notifyUnread}
            onOpenNotif={() => {
              setPanel(panel === "notif" ? null : "notif");
              resetNotifyCount();
            }}
            chatUnread={newMessageCount}
            onOpenChat={() => {
              setPanel(panel === "chat" ? null : "chat");
              resetChatCount();
            }}
            onLogout={() => setConfirmLogout(true)}
          />

          {/*
            內容區＝設計稿的 <section class="el-screen el-page">，每一頁就是它的內容。

            寬度不設上限（使用者要求左右不要留白），所有路由一律撐滿、只給內距；
            頁面看起來沒撐滿，要找的是頁面自己寫死的 max-width。
            原本的 NO_PAGE_CAP 清單在 .el-page 的 max-width 被覆寫成 none 之後，
            兩條分支的結果已經完全一樣，搬到 MUI 時一併收掉。
            內距在窄螢幕跟著降（原 .el-page 的 @media (max-width:900px)）。
            字級不靠 zoom（見 utils/textsize.js）。
          */}
          <Box
            component="section"
            sx={{
              flex: "1 1 auto",
              width: "100%",
              boxSizing: "border-box",
              p: wide ? "var(--space-8) var(--space-10)" : "var(--space-6) var(--space-4)",
            }}
          >
            {/* 工項＝每日日報，今天沒 key 就要在每一頁都看得到，所以放在殼層、
                而且放在 denied 判斷之外 —— 連沒有權限的頁面也照樣提醒 */}
            <DailyTaskBanner />
            {denied ? <NoPermission permission={neededPermission} /> : <Outlet />}
          </Box>
        </Box>

        <RightPanel
          panel={panel}
          onClose={() => setPanel(null)}
          notifications={notifications}
          unreadCount={notifyUnread}
          onMarkAllRead={() => {
            markAllNotifyRead();
            resetNotifyCount();
          }}
          onNotifClick={(n) => {
            if (n.id) markNotifyRead([n.id]);
            if (n.link) {
              navigate(n.link);
              setPanel(null);
            }
          }}
          onLoadMoreNotif={loadMoreNotifications}
          chat={chatMessages}
          onLoadMoreChat={loadMoreMessages}
          chatLoadingMore={chatLoadingMore}
          chatHasMore={chatHasMore}
          chatInput={chatInput}
          onChatInput={setChatInput}
          onSendChat={handleSendChat}
          chatConnected={chatConnected}
          onlineUsers={onlineUsers}
        />

        <CommandPalette
          open={cmdOpen}
          onClose={() => setCmdOpen(false)}
          projects={props.cmdProjects}
          tasks={props.cmdTasks}
        />


        <Toasts toasts={toasts} onClose={dismissToast} />

        <ConfirmDialog
          open={!!confirmState}
          tone={confirmState?.tone}
          title={confirmState?.title}
          subtitle={confirmState?.subtitle}
          body={confirmState?.body}
          cancelText={confirmState?.cancelText}
          confirmText={confirmState?.confirmText}
          onCancel={() => setConfirmState(null)}
          onConfirm={() => {
            const fn = confirmState?.onConfirm;
            setConfirmState(null);
            if (fn) fn();
          }}
        />

        <ConfirmDialog
          open={confirmLogout}
          tone="accent"
          title="確定要登出嗎？"
          subtitle="您將返回登入畫面"
          body="登出後，未儲存的草稿將保留在本機。"
          cancelText="取消"
          confirmText="登出"
          onCancel={() => setConfirmLogout(false)}
          onConfirm={() => {
            setConfirmLogout(false);
            props.Logout(() => navigate("/Auth/Login", { replace: true }));
          }}
        />
      </Box>
    </AppCtx.Provider>
  );
}

/**
 * 後端清單有時回純陣列、有時回 { data_list, total_count } 分頁物件，
 * 直接 .map() 會在分頁形狀下丟 "map is not a function" 把整個 Layout 打掛。
 */
const toArray = (v) => {
  if (Array.isArray(v)) return v;
  if (Array.isArray(_.get(v, "data_list"))) return v.data_list;
  if (Array.isArray(_.get(v, "list"))) return v.list;
  if (Array.isArray(_.get(v, "items"))) return v.items;
  return [];
};

const mapStateToProps = (state) => ({
  isMobile: _.get(state, "Global.isMobile", undefined),
  // 與 Sider 取同一份，兩邊才不會一個藏起來、一個放行
  permissions:
    _.get(state, "Role.PermissionsUser", undefined) || _.get(state, "Auth.Function", undefined),
  isReady: _.get(state, "Global.isReady", false),
  userInfo: _.get(state, "Global.userInfo", {}),
  activities: toArray(_.get(state, "Dashboard.Activities")),
  approvalsCount: toArray(_.get(state, "Approval.ToMe")).length,
  cmdProjects: toArray(_.get(state, "Project.List")).map((p) => ({
    id: p.id,
    name: p.name,
    code: p.code,
  })),
  cmdTasks: toArray(_.get(state, "MyTask.List")).map((t) => ({
    id: t.id,
    name: t.title,
    code: t.issue_key,
    projectId: t.project_id,
  })),
  projectDetail: _.get(state, "Project.Detail", null),
});

const mapDispatchToProps = (dispatch) => ({
  SAVE_Message(payload) {
    dispatch({ type: "SAVE_Message", payload });
  },
  Logout(callback) {
    dispatch({ type: "Logout", callback });
  },
});

Layout.propTypes = {
  permissions: PropTypes.array,
  isReady: PropTypes.bool,
  SAVE_Message: PropTypes.func,
  Logout: PropTypes.func,
  userInfo: PropTypes.object,
  activities: PropTypes.array,
  approvalsCount: PropTypes.number,
  cmdProjects: PropTypes.array,
  cmdTasks: PropTypes.array,
};

Layout.defaultProps = {
  permissions: undefined,
  isReady: false,
  SAVE_Message: () => {},
  Logout: () => {},
  userInfo: {},
  activities: [],
  approvalsCount: 0,
  cmdProjects: [],
  cmdTasks: [],
};

export default connect(mapStateToProps, mapDispatchToProps)(Layout);
